import { useState } from 'react';
import { X, Zap, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import type { Agent, Skill, BriefField } from '../types/cowork';

interface SkillBriefDrawerProps {
  open: boolean;
  skill: Skill;
  agent: Agent | null;
  onClose: () => void;
  onSubmit: (briefData: Record<string, unknown>) => void;
}

export function SkillBriefDrawer({
  open,
  skill,
  agent,
  onClose,
  onSubmit,
}: SkillBriefDrawerProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [contextExpanded, setContextExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setValue = (fieldId: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    setErrors(prev => { const next = { ...prev }; delete next[fieldId]; return next; });
  };

  const toggleMultiselect = (fieldId: string, value: string) => {
    const current = (formData[fieldId] as string[]) || [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setValue(fieldId, next);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    skill.briefFields.forEach(field => {
      if (!field.required) return;
      const val = formData[field.id];
      if (!val || (typeof val === 'string' && val.trim() === '') || (Array.isArray(val) && val.length === 0)) {
        newErrors[field.id] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-[480px] bg-white shadow-[0_0_40px_rgba(0,0,0,0.12)] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-start gap-3">
            {agent && (
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${agent.gradientFrom}20, ${agent.gradientTo}20)`,
                  border: `1.5px solid ${agent.accentColor}25`,
                }}
              >
                {agent.avatarEmoji}
              </div>
            )}
            <div>
              <h2 className="text-[15px] text-[#101828]" style={{ fontWeight: 700 }}>
                {skill.name}
              </h2>
              {agent && (
                <p className="text-[12px] text-[#98a2b3] mt-0.5">
                  with {agent.name} · {agent.title}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-[#98a2b3] hover:text-[#667085] hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Skill meta */}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-[11px] text-[#98a2b3]">
            <Clock className="w-3.5 h-3.5" />
            <span>~{skill.estimatedMinutes} min</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-[#98a2b3]">
            <Zap className="w-3.5 h-3.5" />
            <span>{skill.claudeModel}</span>
          </div>
          {skill.requiredContextNodes.length > 0 && (
            <div className="flex items-center gap-1 text-[11px] text-[#98a2b3]">
              <span>Uses:</span>
              {skill.requiredContextNodes.map(n => (
                <span key={n} className="px-1.5 py-0.5 bg-[#f9fafb] rounded text-[10px]">
                  {n}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Brand context preview (collapsible) */}
        <div className="px-6 py-3 border-b border-gray-100">
          <button
            onClick={() => setContextExpanded(!contextExpanded)}
            className="flex items-center gap-2 text-[12px] text-[#667085] hover:text-[#344054] transition-colors"
            style={{ fontWeight: 500 }}
          >
            {contextExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Brand context that will be injected
          </button>
          {contextExpanded && (
            <div className="mt-2 p-3 bg-[#f9fafb] rounded-xl border border-gray-100">
              <p className="text-[11px] text-[#98a2b3]" style={{ lineHeight: '1.6' }}>
                {skill.requiredContextNodes.map(n => (
                  <span key={n} className="block">
                    ✓ <strong className="text-[#667085]">{n.replace('_', ' ')} memory</strong> — loaded from Brand OS
                  </span>
                ))}
                {skill.requiresBigQueryData && (
                  <span className="block">✓ <strong className="text-[#667085]">performance data</strong> — last 30 days from data lake</span>
                )}
                {skill.requiredContextNodes.length === 0 && !skill.requiresBigQueryData && (
                  <span>No brand context required for this skill.</span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <p className="text-[12px] text-[#98a2b3] mb-5">
            Fill in the brief below — {agent?.name ?? 'the agent'} will use this alongside your brand context.
          </p>

          <div className="space-y-5">
            {skill.briefFields.map(field => (
              <FieldInput
                key={field.id}
                field={field}
                value={formData[field.id]}
                error={errors[field.id]}
                onChange={val => setValue(field.id, val)}
                onToggleMulti={val => toggleMultiselect(field.id, val)}
              />
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="px-6 py-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[14px] transition-all duration-200',
              submitting
                ? 'bg-gray-100 text-[#98a2b3] cursor-not-allowed'
                : 'bg-[#be43ff] text-white hover:bg-[#a835e0] shadow-[0_4px_12px_rgba(190,67,255,0.3)]'
            )}
            style={{ fontWeight: 600 }}
          >
            <Zap className="w-4 h-4" />
            {submitting ? 'Starting...' : `Start with ${agent?.name ?? 'Agent'}`}
          </button>
          <p className="text-center text-[11px] text-[#c4c9d4] mt-2">
            Results stream in real-time
          </p>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
//  Field input renderer
// ─────────────────────────────────────────────

function FieldInput({
  field,
  value,
  error,
  onChange,
  onToggleMulti,
}: {
  field: BriefField;
  value: unknown;
  error?: string;
  onChange: (val: unknown) => void;
  onToggleMulti: (val: string) => void;
}) {
  const baseInputClass = cn(
    'w-full px-3 py-2.5 rounded-xl border text-[13px] text-[#101828] outline-none transition-colors',
    'placeholder:text-[#c4c9d4]',
    error
      ? 'border-red-300 bg-red-50 focus:border-red-400'
      : 'border-gray-200 bg-white focus:border-[#be43ff]'
  );

  return (
    <div>
      <label className="block text-[12px] text-[#344054] mb-1.5" style={{ fontWeight: 600 }}>
        {field.label}
        {field.required && <span className="text-[#be43ff] ml-0.5">*</span>}
      </label>

      {field.type === 'text' || field.type === 'url' || field.type === 'number' ? (
        <input
          type={field.type === 'number' ? 'number' : 'text'}
          value={(value as string) || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder}
          className={baseInputClass}
          style={{ fontWeight: 400 }}
        />
      ) : field.type === 'textarea' ? (
        <textarea
          value={(value as string) || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className={cn(baseInputClass, 'resize-none')}
          style={{ fontWeight: 400 }}
        />
      ) : field.type === 'select' ? (
        <div className="flex flex-col gap-1.5">
          {field.options?.map(opt => (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={cn(
                'text-left px-3 py-2.5 rounded-xl border text-[12px] transition-colors',
                value === opt.value
                  ? 'bg-[#f8f5ff] border-[#be43ff]/30 text-[#be43ff]'
                  : 'border-gray-100 text-[#344054] hover:border-gray-200 hover:bg-[#f9fafb]'
              )}
              style={{ fontWeight: value === opt.value ? 600 : 400 }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : field.type === 'multiselect' ? (
        <div className="flex flex-wrap gap-2">
          {field.options?.map(opt => {
            const selected = ((value as string[]) || []).includes(opt.value);
            return (
              <button
                key={opt.value}
                onClick={() => onToggleMulti(opt.value)}
                className={cn(
                  'px-3 py-1.5 rounded-full border text-[12px] transition-colors',
                  selected
                    ? 'bg-[#be43ff] border-[#be43ff] text-white'
                    : 'border-gray-200 text-[#667085] hover:border-[#be43ff]/30 hover:text-[#be43ff]'
                )}
                style={{ fontWeight: selected ? 600 : 400 }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      ) : null}

      {field.helpText && !error && (
        <p className="text-[11px] text-[#c4c9d4] mt-1">{field.helpText}</p>
      )}
      {error && (
        <p className="text-[11px] text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
}
